import { Controller } from "@hotwired/stimulus"

/**
 * Bulk actions for the support ticket list.
 *
 * data-controller="support--bulk-actions"
 *
 * Manages checkbox selection and a floating action bar for bulk operations.
 */
export default class extends Controller {
  static targets = [
    "checkbox",
    "selectAll",
    "actionBar",
    "count",
    "form",
    "ticketIds",
    "assignDropdown",
    "assignDropdownWrap",
    "assigneeId",
    "macroDropdown",
    "macroDropdownWrap",
    "macroId",
  ]

  connect() {
    this.updateState()
    this._boundCloseDropdown = this.#closeDropdownOnOutsideClick.bind(this)
    document.addEventListener("click", this._boundCloseDropdown)
  }

  disconnect() {
    document.removeEventListener("click", this._boundCloseDropdown)
  }

  // Toggle all checkboxes
  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach((cb) => {
      cb.checked = checked
    })
    this.updateState()
  }

  // Single checkbox changed
  toggle() {
    this.updateState()
  }

  updateState() {
    const selected = this.selectedIds()
    const count = selected.length

    // Update count display (just the number — keeps the bar compact)
    if (this.hasCountTarget) {
      this.countTarget.textContent = count
    }
    // Show per-card checkboxes when bulk mode is active
    this.checkboxTargets.forEach((cb) => {
      cb.classList.toggle("!opacity-100", cb.checked)
    })

    // Show/hide action bar
    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.toggle("hidden", count === 0)
    }

    // Update select-all checkbox state
    if (this.hasSelectAllTarget && this.hasCheckboxTarget) {
      const total = this.checkboxTargets.length
      this.selectAllTarget.checked = count === total && total > 0
      this.selectAllTarget.indeterminate = count > 0 && count < total
    }
  }

  // Submit bulk action
  submit(event) {
    const action = event.currentTarget.dataset.bulkAction
    if (!action) return

    const ids = this.selectedIds()
    if (ids.length === 0) return

    // Populate hidden form fields
    if (this.hasFormTarget && this.hasTicketIdsTarget) {
      // Clear previous hidden inputs
      this.ticketIdsTarget.innerHTML = ""

      ids.forEach((id) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "ticket_ids[]"
        input.value = id
        this.ticketIdsTarget.appendChild(input)
      })

      // Set the action
      const actionInput = this.formTarget.querySelector('[name="bulk_action"]')
      if (actionInput) actionInput.value = action

      this.formTarget.requestSubmit()
    }
  }

  // Toggle the "Assign to..." agent dropdown
  toggleAssignDropdown(event) {
    event.stopPropagation()
    if (this.hasAssignDropdownTarget) {
      this.assignDropdownTarget.classList.toggle("hidden")
    }
  }

  // Assign selected tickets to a specific agent
  assignToAgent(event) {
    const agentId = event.currentTarget.dataset.agentId
    if (!agentId) return

    // Set the assignee hidden field and submit as "assign"
    if (this.hasAssigneeIdTarget) {
      this.assigneeIdTarget.value = agentId
    }

    // Close the dropdown
    if (this.hasAssignDropdownTarget) {
      this.assignDropdownTarget.classList.add("hidden")
    }

    // Create a synthetic event-like object for submit()
    this.#submitAction("assign")
  }

  #submitAction(action) {
    const ids = this.selectedIds()
    if (ids.length === 0) return

    if (this.hasFormTarget && this.hasTicketIdsTarget) {
      this.ticketIdsTarget.innerHTML = ""

      ids.forEach((id) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "ticket_ids[]"
        input.value = id
        this.ticketIdsTarget.appendChild(input)
      })

      const actionInput = this.formTarget.querySelector('[name="bulk_action"]')
      if (actionInput) actionInput.value = action

      this.formTarget.requestSubmit()
    }
  }

  // Toggle the "Macro..." dropdown
  toggleMacroDropdown(event) {
    event.stopPropagation()
    if (this.hasMacroDropdownTarget) {
      this.macroDropdownTarget.classList.toggle("hidden")
    }
  }

  // Apply a macro to selected tickets
  applyMacro(event) {
    const macroId = event.currentTarget.dataset.macroId
    if (!macroId) return

    // Set the macro hidden field and submit as "apply_macro"
    if (this.hasMacroIdTarget) {
      this.macroIdTarget.value = macroId
    }

    // Close the dropdown
    if (this.hasMacroDropdownTarget) {
      this.macroDropdownTarget.classList.add("hidden")
    }

    this.#submitAction("apply_macro")
  }

  #closeDropdownOnOutsideClick(event) {
    if (
      this.hasAssignDropdownTarget &&
      this.hasAssignDropdownWrapTarget &&
      !this.assignDropdownWrapTarget.contains(event.target)
    ) {
      this.assignDropdownTarget.classList.add("hidden")
    }
    if (
      this.hasMacroDropdownTarget &&
      this.hasMacroDropdownWrapTarget &&
      !this.macroDropdownWrapTarget.contains(event.target)
    ) {
      this.macroDropdownTarget.classList.add("hidden")
    }
  }

  selectedIds() {
    return this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
  }
}
